/**
 * DM Sora / Veo / Grok 视频（经后端 /duomi_video）
 * 文档：https://duomiapi.com/doc/98
 */

import { resolveApiUrl } from './apiBaseUrl'
import { finalizeVideoPromptForApi } from './seedanceVideoApi'
import {
  isDuomiVideoModel,
  snapDuomiVideoDuration,
  normalizeDuomiAspectRatio,
  duomiVideoFamily,
  duomiVideoMaxImages,
  sanitizeDuomiVideoPrompt
} from '@/constants/nodeGraphCanvasModelOptions'

function getAuthHeader() {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || ''
}

function parseJsonSafe(text) {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

function pickTaskId(data) {
  if (!data || typeof data !== 'object') return ''
  const inner = data.data && typeof data.data === 'object' ? data.data : null
  return String(
    data.id ||
      data.task_id ||
      data.taskId ||
      (inner && (inner.id || inner.task_id || inner.taskId)) ||
      ''
  ).trim()
}

/** 解开 Dingo / 业务多层 data，拿到带 status / video_url 的业务体 */
function normalizeDuomiTaskQueryPayload(data) {
  if (!data || typeof data !== 'object') return data
  let inner = data
  for (let i = 0; i < 5; i++) {
    if (
      inner &&
      typeof inner === 'object' &&
      inner.data &&
      typeof inner.data === 'object' &&
      !Array.isArray(inner.data) &&
      (inner.status == null ||
        inner.status === '' ||
        (!inner.video_url &&
          !inner.videoUrl &&
          (inner.data.status != null ||
            inner.data.state != null ||
            inner.data.video_url ||
            inner.data.videoUrl ||
            inner.data.message ||
            inner.data.error)))
    ) {
      inner = { ...inner, ...inner.data }
      continue
    }
    break
  }
  const statusRaw =
    inner.status != null && String(inner.status).trim() !== ''
      ? inner.status
      : inner.state != null
        ? inner.state
        : data.status
  const failMsg =
    (inner.message != null && String(inner.message).trim()) ||
    (inner.msg != null && String(inner.msg).trim()) ||
    (inner.error != null && String(inner.error).trim()) ||
    (typeof inner.error === 'object' && inner.error && inner.error.message
      ? String(inner.error.message)
      : '') ||
    ''
  const status = statusRaw != null ? String(statusRaw).toLowerCase() : statusRaw
  // 上游偶发：仍标 running/queued，但文案已是「系统繁忙」软失败
  let statusOut = status
  if (
    failMsg &&
    /系统繁忙|稍后重试|busy|overloaded|quota|限流|rate.?limit/i.test(failMsg) &&
    (!statusOut ||
      statusOut === 'running' ||
      statusOut === 'pending' ||
      statusOut === 'queued' ||
      statusOut === 'processing')
  ) {
    statusOut = 'failed'
  }
  return {
    ...inner,
    id: pickTaskId(inner) || pickTaskId(data) || inner.id,
    status: statusOut != null ? statusOut : inner.status,
    state: inner.state != null ? String(inner.state).toLowerCase() : inner.state,
    message: failMsg || inner.message,
    msg: failMsg || inner.msg,
    error: statusOut === 'failed' || statusOut === 'error' || statusOut === 'fail'
      ? failMsg || inner.error || statusOut
      : inner.error,
    video_url: inner.video_url || inner.videoUrl || inner.result_url || ''
  }
}

/**
 * @param {{ prompt: string, model: string, duration?: number, ratio?: string, resolution?: string, images?: string[], videoMode?: string, signal?: AbortSignal }} opts
 */
export async function createDuomiVideoTask(opts) {
  const {
    prompt,
    model,
    duration,
    ratio,
    resolution,
    images,
    videoMode,
    allowSubtitles,
    signal
  } = opts || {}
  const modelId = String(model || '').trim()
  if (!isDuomiVideoModel(modelId)) {
    throw new Error('不支持的DM视频模型：' + modelId)
  }
  const maxImgs = duomiVideoMaxImages(modelId)
  const imageList = (Array.isArray(images)
    ? images.map(x => (x == null ? '' : String(x).trim())).filter(Boolean)
    : []
  ).slice(0, maxImgs)
  const body = {
    model: modelId,
    prompt: finalizeVideoPromptForApi(sanitizeDuomiVideoPrompt(prompt, modelId), {
      allowSubtitles: !!allowSubtitles
    }),
    duration: snapDuomiVideoDuration(modelId, duration),
    ratio: normalizeDuomiAspectRatio(modelId, ratio),
    resolution: resolution != null ? String(resolution).trim() : '720p',
    video_mode: videoMode != null ? String(videoMode).trim() : '',
    ...(imageList.length ? { images: imageList } : {})
  }
  const res = await fetch(resolveApiUrl('/duomi_video'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getAuthHeader()
    },
    body: JSON.stringify(body),
    signal
  })
  const text = await res.text()
  const data = parseJsonSafe(text) || { raw: text }
  if (!res.ok) {
    const msg =
      (data && (data.message || data.error || data.errors)) ||
      text.slice(0, 400) ||
      `HTTP ${res.status}`
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }
  const taskId = pickTaskId(data)
  if (!taskId) {
    throw new Error('DM视频创建成功但未返回任务 id：' + text.slice(0, 400))
  }
  return { ...data, id: taskId, task_id: taskId, provider: 'duomi' }
}

export async function getDuomiVideoTask(taskId, signal) {
  const id = encodeURIComponent(String(taskId || '').trim())
  if (!id) throw new Error('缺少 task_id')
  const res = await fetch(resolveApiUrl(`/duomi_video/${id}`), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getAuthHeader()
    },
    signal
  })
  const text = await res.text()
  const data = parseJsonSafe(text) || {}
  if (!res.ok) {
    const flat = normalizeDuomiTaskQueryPayload(data)
    const msg =
      (flat && (flat.message || flat.error || flat.msg)) ||
      data.message ||
      data.error ||
      text.slice(0, 400) ||
      `HTTP ${res.status}`
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }
  return normalizeDuomiTaskQueryPayload(data)
}

export function pickDuomiTaskVideoUrl(info) {
  if (!info || typeof info !== 'object') return ''
  const flat = normalizeDuomiTaskQueryPayload(info)
  const c = flat.content && typeof flat.content === 'object' ? flat.content : null
  const d = flat.data && typeof flat.data === 'object' ? flat.data : null
  const videos = (d && d.videos) || flat.videos
  let fromList = ''
  if (Array.isArray(videos) && videos.length) {
    const first = videos[0]
    if (typeof first === 'string') fromList = first
    else if (first && typeof first === 'object') {
      fromList = first.url || first.video_url || first.videoUrl || ''
    }
  }
  const u =
    flat.video_url ||
    flat.videoUrl ||
    fromList ||
    (c && (c.video_url || c.videoUrl || c.url)) ||
    (d && (d.video_url || d.videoUrl || d.url)) ||
    ''
  return String(u || '').trim()
}

export { isDuomiVideoModel, duomiVideoFamily, snapDuomiVideoDuration }
